import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, ShieldCheck, KeyRound, LogIn } from "lucide-react";
import api, { readError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();

  const [step, setStep] = useState("password"); // password | two_factor
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [challengeToken, setChallengeToken] = useState("");
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (params.get("expired")) {
      toast.info("Session expired — please sign in again.");
    }
  }, [params]);

  const onPasswordSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email: email.trim(), password });
      if (data.requires_2fa) {
        setChallengeToken(data.challenge_token);
        setStep("two_factor");
      } else {
        await loginWithToken(data.access_token);
        const next = params.get("next") || "/admin";
        navigate(next, { replace: true });
      }
    } catch (err) {
      setError(readError(err, "Login failed."));
    } finally {
      setBusy(false);
    }
  };

  const onCodeSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-2fa", {
        challenge_token: challengeToken,
        code: code.trim(),
      });
      await loginWithToken(data.access_token);
      const next = params.get("next") || "/admin";
      navigate(next, { replace: true });
    } catch (err) {
      setError(readError(err, "Verification failed."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grain-bg flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md animate-fade-up">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <div className="font-serif text-3xl text-foreground leading-tight">
              Tambure <span className="italic text-primary">Žmegač</span>
            </div>
          </Link>
          <div className="mt-2 text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
            Admin · Phase 0
          </div>
        </div>

        <Card className="bg-card/95 border-border/60 shadow-ambient">
          <CardContent className="p-8 sm:p-10">
            {step === "password" ? (
              <form onSubmit={onPasswordSubmit} className="space-y-6" data-testid="login-form">
                <div>
                  <h1 className="font-serif text-2xl text-foreground">Welcome back</h1>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Sign in to manage the workshop.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="username"
                    required
                    value={email}
                    disabled={busy}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="h-11 bg-card"
                    data-testid="login-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-baseline justify-between">
                    <Label htmlFor="password">Password</Label>
                    <Link
                      to="/admin/forgot-password"
                      data-testid="login-forgot-link"
                      className="text-xs text-muted-foreground hover:text-primary transition-colors"
                    >
                      Forgot?
                    </Link>
                  </div>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    disabled={busy}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••••••"
                    className="h-11 bg-card"
                    data-testid="login-password-input"
                  />
                </div>

                {error && (
                  <div
                    data-testid="login-error"
                    className="text-sm rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-destructive"
                  >
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={busy}
                  data-testid="login-submit-btn"
                  className="w-full h-11 shadow-interactive"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" strokeWidth={1.5} />}
                  {busy ? "Signing in…" : "Sign in"}
                </Button>

                <div className="pt-2 text-center">
                  <Link
                    to="/admin/recovery"
                    data-testid="login-recovery-link"
                    className="text-xs text-muted-foreground hover:text-primary transition-colors"
                  >
                    Lost access to your authenticator?
                  </Link>
                </div>
              </form>
            ) : (
              <form onSubmit={onCodeSubmit} className="space-y-6" data-testid="login-2fa-form">
                <div className="flex items-start gap-3">
                  <div className="rounded-lg bg-primary/10 p-2">
                    <ShieldCheck className="h-5 w-5 text-primary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h1 className="font-serif text-2xl text-foreground">Two-factor required</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {useRecovery
                        ? "Enter one of your saved recovery codes."
                        : "Enter the 6-digit code from your authenticator app."}
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="code">{useRecovery ? "Recovery code" : "Authentication code"}</Label>
                  <Input
                    id="code"
                    type="text"
                    autoComplete="one-time-code"
                    autoFocus
                    required
                    value={code}
                    disabled={busy}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder={useRecovery ? "XXXXX-XXXXX" : "123 456"}
                    className="h-12 bg-card text-center tracking-[0.3em] font-mono text-base"
                    data-testid="login-2fa-input"
                  />
                </div>

                {error && (
                  <div
                    data-testid="login-2fa-error"
                    className="text-sm rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-destructive"
                  >
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={busy}
                  data-testid="login-2fa-submit-btn"
                  className="w-full h-11 shadow-interactive"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" strokeWidth={1.5} />}
                  {busy ? "Verifying…" : "Verify"}
                </Button>

                <button
                  type="button"
                  data-testid="login-toggle-recovery-btn"
                  onClick={() => {
                    setUseRecovery(!useRecovery);
                    setCode("");
                    setError("");
                  }}
                  className="w-full text-xs text-muted-foreground hover:text-primary transition-colors"
                >
                  {useRecovery ? "Use authenticator code instead" : "Use a recovery code instead"}
                </button>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          <Link to="/" className="hover:text-primary transition-colors">
            ← Back to site
          </Link>
        </p>
      </div>
    </div>
  );
}
